import React, {ChangeEvent, Fragment, useEffect, useMemo, useState} from "react";
import {inject, observer} from "mobx-react";
import {reaction} from "mobx";
import {Feature, FeatureGatingStore, FeaturesDataQuery} from "Stores/featureGating";
import {Button, Col, Form, Row, Spinner, Table} from "react-bootstrap";
import {
    CellContext,
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    PaginationState,
    Row as RowInfo,
    useReactTable
} from "@tanstack/react-table";
import {Defaults, Utils} from "../../../common/utils";
import {SimpleCheckbox} from "Components/Common/SimpleCheckbox";
import PageTitle from "Components/Common/PageTitle";
import {useInternalContext} from "@/contexts/internal";

interface FeatureEditProps {
    feature: Feature
    onClose: () => void
    onSave: () => void
    featureGatingStore: FeatureGatingStore
}

function FeatureEdit({feature, onClose, onSave, featureGatingStore}: FeatureEditProps) {
    const [isSaving, setIsSaving] = useState(false);
    const [enabled, setEnabled] = useState(feature.enabled);
    const [allSchools, setAllSchools] = useState(feature.all_schools);
    const [description, setDescription] = useState(feature.description);
    const handleEnabledFieldChange = (next: boolean) => {
        setEnabled(next);
    };

    const handleAllSchoolsFieldChange = (next: boolean) => {
        setAllSchools(next);
    };

    const handleDescriptionFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
        setDescription(e.target.value);
    };

    const handleFeatureSave = async () => {
        setIsSaving(true);
        await featureGatingStore.updateFeature(feature.ruuid, enabled, allSchools, description);
        onSave();
        setIsSaving(false);
    };

    return (
        <Form>
            <Row className="mb-2">
                <Col>
                    <SimpleCheckbox
                        id="enabled-checkbox"
                        label="Enabled"
                        isChecked={ enabled }
                        onChange={handleEnabledFieldChange}
                    />
                </Col>
                <Col>
                    <SimpleCheckbox
                        id="all-schools-checkbox"
                        label="All schools"
                        isChecked={ allSchools }
                        onChange={handleAllSchoolsFieldChange}
                    />
                </Col>
            </Row>
            <Row className="mb-2">
                <Form.Group as={Col}>
                    <Form.Label>Description</Form.Label>
                    <Form.Control placeholder="Description" value={description} onChange={handleDescriptionFieldChange}/>
                </Form.Group>
            </Row>
            <Row className="mb-2">
                <Form.Group as={Col}>
                    <Form.Label>Group: <span className="fst-italic">{feature.component}</span></Form.Label>
                </Form.Group>
                <Form.Group as={Col}>
                    <Form.Label>Feature: <span className="fst-italic">{feature.name}</span></Form.Label>
                </Form.Group>
            </Row>
            <Row className="mb-2">
                <Form.Group as={Col}>
                    <Form.Label>Settings: <span className="fst-italic">{feature.settings ? JSON.stringify(feature.settings) : "{}"}</span></Form.Label>
                </Form.Group>
            </Row>
            <Row className="mb-2 text-end">
                <Col className="text-end">
                    <Button
                        variant="secondary"
                        className="me-2"
                        onClick={ onClose }>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleFeatureSave}>
                        { isSaving ? <Spinner animation="border" size="sm"/> : "Save" }
                    </Button>
                </Col>
            </Row>
        </Form>
    );
}

interface FeaturesListPageProps {
    featureGatingStore?: FeatureGatingStore
}

@inject("featureGatingStore")
@observer
export class FeaturesListPage extends React.Component<FeaturesListPageProps, any> {
    render() {
        if (!this.props.featureGatingStore) {
            return null;
        }

        return (
            <FeaturesList featureGatingStore={this.props.featureGatingStore}/>
        );
    }
}

interface FeaturesListProps {
    featureGatingStore: FeatureGatingStore
}

function FeaturesList({featureGatingStore}: FeaturesListProps) {
    const [featuresDataQuery, setFeaturesDataQuery] = useState<FeaturesDataQuery | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [{pageIndex, pageSize}, setPagination] = useState<PaginationState>({ pageIndex: Defaults.tablePage(), pageSize: Defaults.tablePageSize()});
    const pagination = useMemo(() => ({pageIndex, pageSize}), [pageIndex, pageSize]);
    const {setRenderActionButton} = useInternalContext();
    const columns: ColumnDef<Feature>[] = [
        {
            header: "Group",
            accessorKey: "component",
        },
        {
            header: "Feature",
            accessorKey: "name",
        },
        {
            header: "Description",
            accessorKey: "description",
        },
        {
            header: "Enabled",
            accessorKey: "enabled",
        },
        {
            header: "All schools",
            accessorKey: "all_schools",
        },
        {
            id: "expander",
            header: () => null,
            enableColumnFilter: false,
            cell: (cellContext: CellContext<Feature, unknown>) => {
                return (
                    <div>
                        <button
                            className="table-action-button"
                            onClick={ cellContext.row.getToggleExpandedHandler() }>
                            <i className={`icon icon-${cellContext.row.getIsExpanded() ? "compress" : "expand"}`} />
                        </button>
                    </div>
                );
            }
        }
    ];

    const table = useReactTable({
        columns,
        data: featuresDataQuery?.data ?? [],
        pageCount: Utils.getPageCount(featuresDataQuery?.result?.totalCount ?? 0, pageSize),
        state: {
            pagination
        },
        getCoreRowModel: getCoreRowModel(),
        getRowCanExpand: () => true,
        getExpandedRowModel: getExpandedRowModel(),
        onPaginationChange: setPagination,
        manualPagination: true,
    });

    const handleOnSaveFeature = (row: RowInfo<Feature>) => {
        return () => {
            // close subcomponent and refresh features
            row.toggleExpanded();
            setRefreshKey(prev => prev + 1);
        };
    };

    const renderSubComponent = (row: RowInfo<Feature>) => {
        return (
            <FeatureEdit feature={row.original} onClose={row.getToggleExpandedHandler()} onSave={handleOnSaveFeature(row)} featureGatingStore={featureGatingStore}/>
        );
    };

    const renderTable = () => {
        return (
            <>
                <Table>
                    <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th key={header.id}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody>
                    {table.getRowModel().rows.map(row => (
                        <Fragment key={row.id}>
                            <tr>
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                            {row.getIsExpanded() && (
                                <tr>
                                    <td colSpan={row.getVisibleCells().length}>
                                        {renderSubComponent(row)}
                                    </td>
                                </tr>
                            )}
                        </Fragment>
                    ))}
                    {table.getRowModel().rows.length === 0 && (<tr><td colSpan={6} className="text-center">No Features Available</td></tr>)}
                    </tbody>
                </Table>
                <div className="pagination-bottom">
                    <div className="pagination">
                        <div className="previous d-grid gap-2">
                            <Button disabled={!table.getCanPreviousPage()}>Previous</Button>
                        </div>
                        <div className="center">
                            <span className="page-info">
                                Page
                                <div className="page-jump">
                                    <Form.Control
                                        type="number"
                                        value={table.getState().pagination.pageIndex + 1}
                                        onChange={e => {
                                            const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                            const pageIndex = table.getState().pagination.pageIndex;
                                            if (page > pageIndex) {
                                                if (table.getCanNextPage()) {
                                                    table.setPageIndex(page);
                                                }
                                            } else if(page < pageIndex) {
                                                if (table.getCanPreviousPage()) {
                                                    table.setPageIndex(page);
                                                }
                                            }
                                        }}/>
                                </div>
                                of {" "}
                                <span className="total-pages">{table.getPageCount()}</span>
                            </span>
                        </div>
                        <div className="next d-grid gap-2">
                            <Button disabled={!table.getCanNextPage()}>Next</Button>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    useEffect(() => {
        reaction(() => featureGatingStore.features, newFeatures => {
            setFeaturesDataQuery({
                data: newFeatures,
                result: {totalCount: featureGatingStore.totalFeatures}
            });
        });
    }, []);

    React.useEffect(() => {
        // Set the renderActionButton when this component is mounted
        setRenderActionButton(null);

        // Clear the renderActionButton when this component is unmounted
        return () => {
            setRenderActionButton(null); // Clean up
        };
    }, []);

    useEffect(() => {
        featureGatingStore.fetchFeatures({pageSize, page: pageIndex + 1});
    }, [pageIndex, refreshKey]);

    return (
        <div className="list list-container">
            <PageTitle pageTitle={"Features"} />
            {renderTable()}
        </div>
    );
}